import React, { useState } from "react";
import { PlayCircle, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";
import { earnPoints } from "@/lib/usePoints";
import { trackEvent } from "@/lib/analytics";
import { warn } from "@/lib/log";

const CLIENT = process.env.REACT_APP_ADSENSE_CLIENT;
const SLOT = process.env.REACT_APP_ADSENSE_SLOT_REWARDED;
export const REWARD_POINTS = 3;

/** Loads an AdSense rewarded ad; resolves with the ad object or null when none is available. */
const loadRewarded = () => new Promise((resolve) => {
  const timer = setTimeout(() => resolve(null), 12000);
  try {
    (window.adsbygoogle = window.adsbygoogle || []).push({ params: { google_ad_slot: SLOT, google_ad_format: "rewarded", google_ad_loaded_callback: (ad) => { clearTimeout(timer); resolve(ad || null); } } });
  } catch (err) { warn("rewarded push failed:", err); clearTimeout(timer); resolve(null); }
});

/** Opt-in "watch an ad → +3 points" (badges only). Hidden until REACT_APP_ADSENSE_SLOT_REWARDED is set. */
export default function RewardedAd({ className = "" }) {
  const { lang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!CLIENT || !SLOT) return null;
  const watch = async () => {
    setBusy(true); trackEvent("rewarded_optin");
    const ad = await loadRewarded();
    setBusy(false);
    if (!ad) { toast(t("reward_none")); return; }
    setOpen(false);
    ad.show((result) => {
      if (result?.status === "viewed") { trackEvent("rewarded_viewed"); earnPoints("ad_reward", lang); }
      else trackEvent("rewarded_skipped");
    });
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#5B4B8A] text-white text-sm font-medium hover:bg-ink transition-colors ${className}`} data-testid="rewarded-btn">
        <PlayCircle className="w-4 h-4" /> {t("reward_btn").replace("{n}", REWARD_POINTS)}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="rewarded-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Sparkles className="w-5 h-5 text-ochre" /> {t("reward_title")}</DialogTitle><DialogDescription>{t("reward_sub").replace("{n}", REWARD_POINTS)}</DialogDescription></DialogHeader>
          <p className="text-xs text-muted-foreground">{t("reward_disclosure")}</p>
          <div className="flex gap-2">
            <button onClick={() => setOpen(false)} className="flex-1 rounded-full border border-stoneLine px-4 py-2.5 text-sm hover:bg-sandDeep/50" data-testid="rewarded-decline">{t("reward_no")}</button>
            <button onClick={watch} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-ink text-sand px-4 py-2.5 text-sm font-semibold hover:bg-ochre disabled:opacity-60" data-testid="rewarded-accept">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} {t("reward_yes")}</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
